/**
 * Data layer for `custom-report-columns`. Thin typed fetch wrappers over /api/v1/custom-report-columns.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomReportColumnsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomReportColumnsResponse {
	count: number;
	items: CustomReportColumnsRow[];
}

export interface CreateCustomReportColumnsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-report-columns";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomReportColumnsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomReportColumnsResponse>(res);
}

export async function create(body: CreateCustomReportColumnsBody): Promise<CustomReportColumnsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomReportColumnsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customReportColumnsQueryKeys = {
	all: ["custom-report-columns"] as const,
	list: () => [...customReportColumnsQueryKeys.all] as const,
	detail: (id: string) => [...customReportColumnsQueryKeys.all, id] as const,
};
